import { Fragment, useEffect, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/base/button";

interface ToolSheetShellProps {
  title: string;
  onClose: () => void;
  /** Main scrollable content. */
  children: ReactNode;
  /** Sticky footer slot — typically the action button(s). */
  footer?: ReactNode;
}

/**
 * Right-side panel chrome shared across tool sheets. 640px wide on desktop,
 * full-height, with a click-to-dismiss backdrop and escape-to-close.
 * Mirrors `presentation/merchants/components/TrackSheet.tsx`.
 */
export function ToolSheetShell({
  title,
  onClose,
  children,
  footer,
}: ToolSheetShellProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <Fragment>
      <div
        className="fixed inset-0 z-40 bg-black/10"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-label={title}
        className="border-border bg-card fixed top-0 right-0 bottom-0 z-50 flex w-[640px] max-w-[95vw] flex-col border-l shadow-xl"
      >
        <header className="border-border flex items-center justify-between border-b px-5 py-4">
          <h2 className="text-foreground text-base font-semibold">{title}</h2>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <footer className="border-border border-t px-5 py-3">{footer}</footer>
        )}
      </aside>
    </Fragment>
  );
}
